import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import Toast from "../CommonElements/Toast";

const ToastContext = createContext(null);
const TOAST_DURATION = 4500;
const RICH_TOAST_DURATION = 16000;
const VISIBLE_STACK = 3;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map());

  const dismissToast = useCallback((id) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(
    (message, variant = "success", options = {}) => {
      const extras = typeof variant === "object" ? variant : options;
      const nextVariant = typeof variant === "object" ? extras.variant || "success" : variant;
      const details = Array.isArray(extras.details) ? extras.details : [];
      const duration =
        extras.duration ?? (details.length ? RICH_TOAST_DURATION : TOAST_DURATION);
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const nextToast = {
        id,
        message,
        variant: nextVariant,
        title: extras.title || "",
        details,
      };

      setToasts((prev) => {
        const kept = prev.slice(-(VISIBLE_STACK - 1));
        prev.slice(0, Math.max(0, prev.length - (VISIBLE_STACK - 1))).forEach((old) => {
          const timer = timersRef.current.get(old.id);
          if (timer) {
            clearTimeout(timer);
            timersRef.current.delete(old.id);
          }
        });
        return [...kept, nextToast];
      });

      if (duration > 0) {
        timersRef.current.set(
          id,
          setTimeout(() => dismissToast(id), duration)
        );
      }
    },
    [dismissToast]
  );

  useEffect(
    () => () => {
      timersRef.current.forEach((timer) => clearTimeout(timer));
      timersRef.current.clear();
    },
    []
  );

  const value = useMemo(() => ({ showToast }), [showToast]);
  const visibleToasts = toasts.slice(-VISIBLE_STACK);
  const hasRichToast = visibleToasts.some((toast) => toast.details?.length);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {typeof document !== "undefined" && visibleToasts.length
        ? createPortal(
            <div
              className={`toast-stack${hasRichToast ? " toast-stack--rich" : ""}`}
              aria-live="polite"
              aria-relevant="additions"
            >
              {visibleToasts.map((toast) => (
                <Toast
                  key={toast.id}
                  variant={toast.variant}
                  title={toast.title}
                  message={toast.message}
                  details={toast.details}
                  onDismiss={() => dismissToast(toast.id)}
                />
              ))}
            </div>,
            document.body
          )
        : null}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return context;
};
